import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { ArrowLeft, PanelRightClose } from 'lucide-react'
import { Thread } from '../components/chat/Thread'
import { useFirstQuestions, useThreads, type ChatThread } from '../lib/chat'
import { listTitle, listedThreads, todaysThread } from '../lib/chatClient'
import { fmtDayShort } from '../lib/today'
import { Failed } from '../screens/Stub'
import '../components/chat/chat.css'

/** How a pane on the working surface hands the panel something to ask about:
 *  the panel opens with the words in the composer, unsent. */
export const AskAboutContext = createContext<(text: string) => void>(() => {})
export const useAskAbout = () => useContext(AskAboutContext)

/** a3-26 (Doug chose): three questions on an empty thread, sent as pressed. */
const STARTERS = ["What's on tonight?", 'Anything good this weekend?', 'What should I book for next week?'] as const

/** The Ask tab's thread choice (`screens/Butler.tsx`), in a panel: today's by
 *  default, an explicit choice remounts `Thread`, and the Worker naming the
 *  thread a turn went to is adopted without a remount. */
export function AskPanel({ open, onHide, seed, onSeeded }: {
  open: boolean
  onHide: () => void
  /** Words for the composer; `onSeeded` follows once `Thread` has taken them,
   *  so the same words asked about twice still land. */
  seed?: string
  onSeeded?: () => void
}) {
  const threadsQ = useThreads()
  const [chosen, setChosen] = useState<{ id: string | null } | undefined>(undefined)
  const [generation, setGeneration] = useState(0)
  // a3-26 (Doug chose: the list inside the panel). The phone's bottom sheet
  // opened in the middle of the window, dimming Browse, while the panel that
  // opened it sat at the right edge. The list now takes the thread's place
  // and Back returns; the thread stays mounted under it, so a turn still
  // streaming is not cut off by looking at the list.
  const [listing, setListing] = useState(false)

  const today = todaysThread(threadsQ.data, new Date())
  const threadId = chosen ? chosen.id : today?.id ?? null
  const thread = threadsQ.data?.find(t => t.id === threadId) ?? null

  const choose = (id: string | null) => {
    setChosen({ id })
    setGeneration(g => g + 1)
  }
  const adopt = useCallback((id: string) => setChosen({ id }), [])
  // A child's effects run before its parent's, so `Thread` has the seed by now.
  // Words asked about land in the thread, so the list gives way to it.
  useEffect(() => { if (seed) { setListing(false); onSeeded?.() } }, [seed, onSeeded])

  return (
    <aside className="desk-ask" aria-label="Ask" hidden={!open}>
      {/* One header: `Thread` draws its own (title, New, Threads). This row
          holds the way to close the panel and, over the list, the way back. */}
      <div className="ah">
        {listing && (
          <button type="button" className="hide back" onClick={() => setListing(false)}>
            <ArrowLeft aria-hidden="true" /> Back
          </button>
        )}
        <button type="button" className="hide" onClick={onHide} aria-label="Hide Ask">
          <PanelRightClose aria-hidden="true" /> Hide
        </button>
      </div>
      {listing && (
        <ThreadList
          threads={threadsQ.data ?? []}
          error={threadsQ.data ? null : threadsQ.error}
          current={threadId}
          onPick={id => {
            setListing(false)
            if (id !== threadId) choose(id)
          }}
        />
      )}
      <div className="body" hidden={listing}>
        <Thread
          key={generation}
          threadId={threadId}
          title={thread?.title ?? null}
          loading={!chosen && threadsQ.isPending}
          listError={!chosen && !threadsQ.data ? threadsQ.error : null}
          fresh={chosen?.id === null}
          onNew={() => choose(null)}
          onThreads={() => setListing(true)}
          onThreadId={adopt}
          seed={seed}
          starters={STARTERS}
        />
      </div>
    </aside>
  )
}

/** Every thread with something in it, most recent first, by its first
 *  question; the one open is marked. */
function ThreadList({ threads, error, current, onPick }: {
  threads: ChatThread[]
  error: Error | null
  current: string | null
  onPick: (id: string) => void
}) {
  const shown = listedThreads(threads)
  const firstQ = useFirstQuestions(shown.map(t => t.id))
  return (
    <div className="threads" role="region" aria-label="Threads">
      <h2>Threads</h2>
      {error ? <Failed error={error.message} />
        : shown.length === 0 ? <p className="none">No threads yet.</p>
          : (
            <ul>
              {shown.map(t => (
                <li key={t.id}>
                  <button type="button" aria-current={t.id === current ? 'true' : undefined} onClick={() => onPick(t.id)}>
                    <span className="q">{listTitle(t.title, firstQ.data?.get(t.id) ?? null) ?? 'Untitled'}</span>
                    <span className="d">{t.id === current ? 'Open · ' : ''}{fmtDayShort(t.last_at)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
    </div>
  )
}
